"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";
import { invalidateActivityLogQueries } from "@/app/hooks/settings/activityLogQueryKeys";
import {
  AuthenticationError,
  PermissionError,
  isHttpSessionAuthError,
  clearAuthAndRedirect,
} from "@/app/utils/auth-error-handler";

interface TanstackProviderProps {
  children: React.ReactNode;
}

/**
 * Global error handler for React Query
 * Handles authentication errors by clearing auth state and redirecting to login
 */
function handleGlobalQueryError(error: unknown): void {
  if (error instanceof PermissionError) {
    console.error("[TanstackProvider] Permission error (session kept):", error.message);
    return;
  }

  if (isHttpSessionAuthError(error) || error instanceof AuthenticationError) {
    console.log(
      "[TanstackProvider] Authentication error detected in query, redirecting to login"
    );
    clearAuthAndRedirect();
    return;
  }

  console.error("[TanstackProvider] Query error:", error);
}

/**
 * Custom retry function that doesn't retry auth errors
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (
    error instanceof PermissionError ||
    isHttpSessionAuthError(error) ||
    error instanceof AuthenticationError
  ) {
    return false;
  }

  // Retry other errors up to 1 time
  return failureCount < 1;
}

// Global instance to ensure singleton pattern
let queryClientInstance: QueryClient | undefined = undefined;

function createQueryClient(): QueryClient {
  if (!queryClientInstance) {
    queryClientInstance = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 60 * 1000, // 1 minute
          retry: shouldRetry,
          refetchOnWindowFocus: false,
        },
        mutations: {
          retry: shouldRetry,
          onError: handleGlobalQueryError,
          onSuccess: () => {
            if (queryClientInstance) {
              void invalidateActivityLogQueries(queryClientInstance);
            }
          },
        },
      },
    });

    // Set up global query cache error handler
    queryClientInstance.getQueryCache().config.onError = handleGlobalQueryError;
    queryClientInstance.getMutationCache().config.onError =
      handleGlobalQueryError;
  }
  return queryClientInstance;
}

export const TanstackProvider = ({ children }: TanstackProviderProps) => {
  // Create QueryClient synchronously on client side
  // Since this is wrapped in ClientOnly, window should always be defined
  const [queryClient] = useState<QueryClient>(() => {
    if (typeof window === "undefined") {
      // Fallback for SSR (shouldn't happen with ClientOnly)
      return new QueryClient({
        defaultOptions: {
          queries: { enabled: false, retry: false },
          mutations: { retry: false },
        },
      });
    }
    
    try {
      const client = createQueryClient();
      // Force initialization by ensuring all internal properties exist
      // This prevents "undefined subscribe" errors
      if (client && (client as any).queryCache) {
        // QueryClient is initialized
      }
      return client;
    } catch (error) {
      console.error("[TanstackProvider] Error creating QueryClient:", error);
      // Return a minimal fallback
      return new QueryClient({
        defaultOptions: {
          queries: { enabled: false, retry: false },
          mutations: { retry: false },
        },
      });
    }
  });

  // Always render QueryClientProvider to avoid breaking hooks
  // The ClientOnly wrapper ensures this only renders on client
  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};
